import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import PaginatedSortableTable from "../components/PaginatedSortableTable";
import PaginatedSortableControls from "../components/PaginatedSortableControls";

export default function NoKor() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const { setIsLoggedIn } = useOutletContext();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [VendorList, setVendorList] = useState([]);
  const [gameList, setGameList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const rowsPerPageOptions = [10, 20, 50, 100];

  const [showModal, setShowModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [korName, setKorName] = useState("");
  const [saving, setSaving] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    field: "game_id",
    order: "asc",
  });

  /* =========================
   * API
   * ========================= */

  useEffect(() => {
    const storedUser = localStorage.getItem("first");

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (e) {
        console.error("유저 정보 파싱 오류:", e);
      }
    }
  }, []);
  const fetchVendorList = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(`${API_BASE}/api/MyVendorList`, {
        params: {
          userId: user.user_id,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setVendorList(res.data.List);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if (
          err.response.status === 401 ||
          err.response.status === 403 ||
          err.response.status === 500
        ) {
          localStorage.removeItem("token");
          setIsLoggedIn(false);
          navigate("/login");
          return;
        }
      }
      console.error("유저 정보 불러오기 실패:", err);
    }
  };

  const fetchGameList = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/api/GameListNoKor`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (Array.isArray(res.data?.vendor_list)) {
        setGameList(res.data.vendor_list);
        setCurrentPage(1);
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if ([401, 403, 500].includes(err.response.status)) {
          localStorage.removeItem("token");
          setIsLoggedIn(false);
          navigate("/login");
          return;
        }
      }
      console.error("GameListNoKor 조회 실패:", err);
    }
  };

  useEffect(() => {
    fetchGameList();
    fetchVendorList();
  }, [user]);

  /* =========================
   * 정렬 / 페이징
   * ========================= */
  const vendorMap = React.useMemo(() => {
    const map = new Map();

    VendorList.forEach((v) => {
      if (v.v_code) {
        map.set(v.v_code.trim(), v.v_name_kor?.trim() || v.v_code);
      }
    });

    return map;
  }, [VendorList]);
  const sortedData = [...gameList].sort((a, b) => {
    const aVal = a[sortConfig.field] ?? "";
    const bVal = b[sortConfig.field] ?? "";

    if (sortConfig.order === "asc") return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const pagedData = sortedData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  /* =========================
   * Row
   * ========================= */
  const renderRow = (row, idx) => {
    const vendorKorName = vendorMap.get(row.vcode) || row.vcode;

    return (
      <tr key={`${row.vcode}_${row.game_id}_${idx}`}>
        <td className="text-center">{row.game_id}</td>

        <td className="text-center">
          <span className="badge bg-outline-info">{vendorKorName}</span>
        </td>

        <td className="text-center">{row.game_name || "-"}</td>

        <td className="text-center">
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => {
              setSelectedGame(row);
              setKorName("");
              setShowModal(true);
            }}
          >
            등록
          </button>
        </td>
      </tr>
    );
  };

  /* =========================
   * Render
   * ========================= */
  return (
    <div className="nk-content">
      <div className="container-fluid">
        <div className="nk-content-inner">
          <div className="nk-content-body">
            <div className="nk-block">
              <div className="card card-bordered">
                <div className="card-inner-group">
                  <PaginatedSortableControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                    sortConfig={sortConfig}
                    sortOptions={[
                      { value: "game_id:asc", label: "게임ID ↑" },
                      { value: "game_id:desc", label: "게임ID ↓" },
                      { value: "vcode:asc", label: "밴더 ↑" },
                      { value: "vcode:desc", label: "밴더 ↓" },
                    ]}
                    onPageChange={setCurrentPage}
                    onRowsPerPageChange={(v) => {
                      setRowsPerPage(v);
                      setCurrentPage(1);
                    }}
                    onSortChange={(field, order) =>
                      setSortConfig({ field, order })
                    }
                  />

                  <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr>
                          <th className="text-center">게임아이디</th>
                          <th className="text-center">밴더사</th>
                          <th className="text-center">게임명(EN)</th>
                          <th className="text-center">게임명(KR)</th>
                        </tr>
                      </thead>

                      <PaginatedSortableTable
                        data={pagedData}
                        renderRow={renderRow}
                      />
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">게임명(KR) 등록</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                <div className="mb-2">
                  <strong>게임 ID:</strong> {selectedGame?.game_id}
                </div>
                <div className="mb-2">
                  <strong>밴더:</strong> {selectedGame?.vcode}
                </div>

                <input
                  type="text"
                  className="form-control"
                  placeholder="게임명(한글)을 입력하세요"
                  value={korName}
                  onChange={(e) => setKorName(e.target.value)}
                />
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  취소
                </button>

                <button
                  className="btn btn-primary"
                  disabled={!korName || saving}
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const token = localStorage.getItem("token");

                      await axios.post(
                        `${API_BASE}/api/GameKorUpdate`,
                        {
                          game_id: selectedGame.game_id,
                          vcode: selectedGame.vcode,
                          game_name_kor: korName,
                        },
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                        },
                      );

                      setShowModal(false);
                      fetchGameList(); // 🔥 즉시 갱신
                    } catch (e) {
                      alert("저장 실패");
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
